import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { MapPin, ArrowRight, ArrowLeft, SealCheck, Phone } from "@phosphor-icons/react";

export default function DirectoryCity() {
  const { state, city } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/directory/${state}/${city}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [state, city]);

  useSeo({
    title: data ? `Dry ice blasting in ${data.city}, ${data.state} · ${data.listings.length} verified pro${data.listings.length === 1 ? "" : "s"}` : "Dry Ice Connect",
    description: data ? `Hire a verified dry ice blasting contractor in ${data.city}, ${data.state}. ${data.listings.length} pro${data.listings.length === 1 ? "" : "s"} offering mobile and in-shop service for automotive, industrial, marine, mold, smoke and food-safe cleaning.` : "",
    canonical: `${window.location.origin}/pros/${state}/${city}`,
  });

  if (notFound) return <Navigate to={`/pros/${state}`} replace />;
  if (!data) return <Layout><div className="max-w-3xl mx-auto px-4 py-24 font-mono text-xs text-[#52525B] text-center">[LOADING]</div></Layout>;

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Dry ice blasting pros in ${data.city}, ${data.state}`,
    itemListElement: data.listings.map((l, idx) => ({
      "@type": "ListItem", position: idx + 1,
      item: {
        "@type": "LocalBusiness",
        name: l.business_name,
        url: `${window.location.origin}/pro/${l.slug}`,
        telephone: l.phone,
        address: { "@type": "PostalAddress", addressLocality: l.city, addressRegion: l.state, postalCode: l.zip, addressCountry: "US" },
        description: l.about?.slice(0, 160),
      },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }} />
      <section className="border-b border-[#262626] bg-grid bg-grid-fade">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14">
          <Link to={`/pros/${data.state_slug}`} className="font-mono text-xs text-[#52525B] hover:text-[#00D4FF] inline-flex items-center gap-1 mb-4" data-testid="city-back">
            <ArrowLeft size={12} weight="bold" /> {data.state_name}
          </Link>
          <div className="section-label mb-3">— {data.city.toUpperCase()}, {data.state}</div>
          <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tighter" data-testid="city-headline">
            Dry ice blasting in <span className="text-[#00D4FF]">{data.city}</span>.
          </h1>
          <p className="font-plex text-base text-[#9CA3AF] mt-4 max-w-2xl">
            {data.listings.length} verified pro{data.listings.length === 1 ? "" : "s"} serving {data.city}, {data.state_name}. Click any profile to request a direct quote — no middleman, no fee.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12">
        <div className="section-label mb-5">— {data.listings.length} PRO{data.listings.length === 1 ? "" : "S"}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="city-listings">
          {data.listings.map((l) => (
            <article key={l.id} className="surface p-6" data-testid={`city-pro-${l.slug}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-chivo font-bold text-xl text-white uppercase tracking-tight">{l.business_name}</h3>
                {l.verified && <SealCheck size={18} weight="fill" className="text-[#00D4FF] shrink-0" />}
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {(l.service_types || []).map((t) => <span key={t} className="tag tag-active">{t.toUpperCase()}</span>)}
                {(l.service_style || []).map((s) => <span key={s} className="tag">{s === "mobile" ? "MOBILE" : "IN-SHOP"}</span>)}
              </div>
              <p className="font-plex text-sm text-[#9CA3AF] line-clamp-3">{l.about || "Professional dry ice blasting services."}</p>
              <div className="flex items-center gap-4 mt-5">
                {l.phone && (
                  <a href={`tel:${l.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-1 font-mono text-sm text-[#00D4FF] hover:underline">
                    <Phone size={14} weight="duotone" /> {l.phone}
                  </a>
                )}
                <Link to={`/pro/${l.slug}`} className="btn-primary text-xs ml-auto" data-testid={`city-view-pro-${l.slug}`}>
                  View Profile <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </Layout>
  );
}
